import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { formatMoney, CURRENCIES } from "@/lib/format";
import { toast } from "sonner";

const CONFIG = {
  customer: { endpoint: "/customers", route: "musteriler", title: "Müşteriler", singular: "Müşteri" },
  supplier: { endpoint: "/suppliers", route: "tedarikciler", title: "Tedarikçiler", singular: "Tedarikçi" },
  employee: { endpoint: "/employees", route: "calisanlar", title: "Çalışanlar", singular: "Çalışan" },
};

const emptyForm = { name: "", phone: "", email: "", address: "", tax_no: "", notes: "", currency: "TRY", position: "", salary: 0 };

export default function EntityList({ kind }) {
  const cfg = CONFIG[kind];
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = async () => {
    const url = kind === "employee" ? "/employees-summary" : cfg.endpoint;
    const { data } = await api.get(url, { params: q ? { q } : {} });
    const items = kind === "employee" && q
      ? data.filter(x => (x.name || "").toLowerCase().includes(q.toLowerCase()))
      : data;
    setItems(items);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, q]);

  const openNew = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = async (it) => {
    setEditingId(it.id);
    // Fetch full record (list endpoint may omit fields for employee summary)
    try {
      const { data } = await api.get(`${cfg.endpoint}/${it.id}`);
      setForm({
        name: data.name || "",
        phone: data.phone || "",
        email: data.email || "",
        address: data.address || "",
        tax_no: data.tax_no || "",
        notes: data.notes || "",
        currency: data.currency || "TRY",
        position: data.position || "",
        salary: data.salary || 0,
      });
    } catch {
      setForm({ ...emptyForm, name: it.name || "" });
    }
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("Ad zorunlu"); return; }
    const body = { ...form, salary: Number(form.salary || 0) };
    try {
      if (editingId) {
        await api.put(`${cfg.endpoint}/${editingId}`, body);
        toast.success(`${cfg.singular} güncellendi`);
      } else {
        await api.post(cfg.endpoint, body);
        toast.success(`${cfg.singular} eklendi`);
      }
      setOpen(false); setEditingId(null); setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  const doDelete = async () => {
    const it = confirmTarget;
    if (!it) return;
    setConfirmTarget(null);
    try {
      await api.delete(`${cfg.endpoint}/${it.id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid={`${kind}-list-page`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">Cari</div>
          <h1 className="text-3xl mt-1">{cfg.title}</h1>
        </div>
        <Button data-testid={`new-${kind}-button`} onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800"><Plus size={16} className="mr-1" />Yeni {cfg.singular}</Button>
      </div>
      <Input data-testid={`${kind}-search-input`} placeholder={kind === "customer" ? "İsim, telefon veya vergi/TC no ile ara..." : "Ara..."} value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-sm" />
      <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
        <table className="acc">
          <thead>
            {kind === "employee" ? (
              <tr>
                <th>Ad</th><th>Pozisyon</th>
                <th className="num">Yıllık Maaş</th>
                <th className="num">Yıllık Bonus</th>
                <th className="num">Toplam Borç/Alacak</th>
                <th style={{width:90}}></th>
              </tr>
            ) : (
              <tr><th>Ad</th><th>Telefon</th><th>E-posta</th><th className="num">Bakiye</th><th style={{width:90}}></th></tr>
            )}
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={kind==="employee"?6:5} className="text-center text-zinc-500 py-8">Henüz kayıt yok</td></tr>}
            {items.map((it) => (
              <tr key={it.id} data-testid={`${kind}-row-${it.id}`} className="group">
                <td><Link to={`/${cfg.route}/${it.id}`} className="text-zinc-900 hover:underline font-medium">{it.name}</Link></td>
                {kind === "employee" ? (
                  <>
                    <td className="text-zinc-600">{it.position || "-"}</td>
                    <td className="num">{formatMoney(it.salary_year, it.currency)}</td>
                    <td className="num">{formatMoney(it.bonus_year, it.currency)}</td>
                    <td className={`num ${(it.borrow_from_balance||0) > 0 ? "neg" : "pos"}`} title="Şirketin çalışana borcu (cari dışı)">{formatMoney(it.borrow_from_balance, it.currency)}</td>
                  </>
                ) : (
                  <>
                    <td className="text-zinc-600">{it.phone || "-"}</td>
                    <td className="text-zinc-600">{it.email || "-"}</td>
                    <td className={`num ${(it.balance || 0) >= 0 ? "pos" : "neg"}`}>{formatMoney(it.balance, it.currency)}</td>
                  </>
                )}
                <td>
                  <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      data-testid={`${kind}-edit-${it.id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(it); }}
                      className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-zinc-900"
                      title="Düzenle"
                    ><Pencil size={14} /></button>
                    <button
                      type="button"
                      data-testid={`${kind}-delete-${it.id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmTarget(it); }}
                      className="p-1.5 rounded hover:bg-red-50 text-zinc-500 hover:text-red-600"
                      title="Sil"
                    ><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
        <DialogContent data-testid={`${kind}-form-dialog`}>
          <DialogHeader><DialogTitle>{editingId ? `${cfg.singular} Düzenle` : `Yeni ${cfg.singular}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Ad</Label><Input data-testid={`${kind}-name-input`} value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} /></div>
            <div><Label>Telefon</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} /></div>
            <div><Label>E-posta</Label><Input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} /></div>
            <div className="col-span-2"><Label>Adres</Label><Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} /></div>
            <div><Label>{kind === "customer" ? "Vergi No / TC" : "Vergi No"}</Label><Input value={form.tax_no} onChange={(e)=>setForm({...form, tax_no: e.target.value})} /></div>
            <div>
              <Label>Para Birimi</Label>
              <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {kind==="employee" && (
              <>
                <div><Label>Pozisyon</Label><Input value={form.position} onChange={(e)=>setForm({...form, position: e.target.value})} /></div>
                <div><Label>Maaş</Label><Input type="number" step="0.01" value={form.salary} onChange={(e)=>setForm({...form, salary: e.target.value})} /></div>
              </>
            )}
            <div className="col-span-2"><Label>Notlar</Label><Textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>İptal</Button>
            <Button data-testid={`${kind}-save-button`} onClick={save} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmTarget} onOpenChange={(v)=>{ if (!v) setConfirmTarget(null); }}>
        <AlertDialogContent data-testid={`${kind}-delete-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{cfg.singular} Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmTarget?.name}" silinsin mi? Bu işlem geri alınamaz. Geçmiş hareketler ve faturalar korunur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${kind}-delete-cancel`}>İptal</AlertDialogCancel>
            <AlertDialogAction data-testid={`${kind}-delete-confirm-btn`} onClick={doDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
